import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import { SmartMetricsBadge } from '@/src/components/common/srp/smartMetrics'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useGetRTSolutionCardData } from '@/src/hooks/srp/RTSolutionView/getRTSolutionCardData'
import { FlightCard } from '@/src/types/flight'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import type { ProcessedFlightMetrics } from '@/src/utils/srp/aiRecommendation/metrics'
import { getFormattedDate } from '@/src/utils/date'
import { replacePlaceHolderString } from '@/src/utils/generalUtils'
import CircleIcon from '@mui/icons-material/Circle'
import NoLuggageOutlinedIcon from '@mui/icons-material/NoLuggageOutlined'
import { Box, Button, CardContent, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useCallback } from 'react'

interface RtSolutionCardProps {
    card: FlightCard
    onSelectCard?: (card: FlightCard) => void
    onViewDetailsClick?: (card: FlightCard[], showBaggage?: boolean) => void
    loading?: boolean
    smartMetrics?: ProcessedFlightMetrics
}

const RtSolutionCard: FC<RtSolutionCardProps> = ({ card, onSelectCard, onViewDetailsClick, loading, smartMetrics }) => {
    const tCommon = useTranslations('common')
    const tFlightDetails = useTranslations('srpFlightDetails')
    const { formatCurrency } = useFormatCurrencyForSrp()
    const flightData = useGetRTSolutionCardData(card)
    const { formatDate } = useFormatDate()
    const handleSelectCard = useCallback(() => {
        !loading && onSelectCard?.(card)
    }, [card, onSelectCard, loading])

    const handleViewDetailsClick = useCallback(() => {
        !loading && onViewDetailsClick?.([card], false)
    }, [card, onViewDetailsClick, loading])

    function getLayoverTypeAndMessage (layoverMinutes: number) {
        if (layoverMinutes > 240) {
            return {
                type: 'longLayover',
                message: tFlightDetails('longLayoverCta'),
                iconColor: 'warning.main'
            }
        }
        return {
            type: 'shortLayover',
            message: tFlightDetails('shortLayoverCta'),
            iconColor: 'info.main'
        }
    }

    return (
        <Box
            sx={{
                p: 0,
                bgcolor: 'white',
                cursor: 'pointer',

                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'grey.300'
                }
            }}
        >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, minWidth: 'max-content' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    {/* Left section - Flight details */}
                    <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                        {/* Airline logo */}
                        <Box
                            display="flex"
                            alignItems="center"
                            flexDirection="column"
                            width="100px"
                            sx={{ overflowX: 'wrap', wordBreak: 'break-word' }}
                        >
                            <Box display={'flex'} maxWidth={150} overflow={'auto'} gap={0.5}>
                                {getAirlineIcons(flightData.airlineCodes, 32, 32)}
                            </Box>
                            <Typography textAlign="center" variant="body2" color="grey.600">
                                {flightData.airlineName}
                            </Typography>
                        </Box>

                        {/* Flight info */}
                        <Box flex={1}>
                            {/* Time and route */}
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Box display="flex" alignItems="center" flexDirection="column" width="60px">
                                    <Typography
                                        variant="h6"
                                        component="span"
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 700,
                                            color: 'black',
                                            display: 'flex',
                                            flexDirection: 'row'
                                        }}
                                    >
                                        {flightData.departureTime}
                                    </Typography>
                                    {flightData.isNbDeparture ? (
                                        <Tooltip
                                            title={
                                                <Box>
                                                    <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                        {tFlightDetails('nearbyAirport')}
                                                    </Typography>
                                                    <Typography fontWeight={500} color="inherit" fontSize={"10px"}>
                                                        {flightData.departureAirport}- {flightData.departureAirportName}
                                                    </Typography>
                                                    <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize={"10px"}>
                                                        {tFlightDetails('nearbyAirportCta')}
                                                    </Typography>
                                                </Box>
                                            }
                                            placement="top"

                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: '#184E91',
                                                    background: '#EFF6FF',
                                                    px: 1,
                                                    borderRadius: 1,
                                                    display: 'inline-block',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {flightData.departureAirport}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                                color: 'grey.600',
                                                display: 'inline-block'
                                            }}
                                        >
                                            {flightData.departureAirport}
                                        </Typography>
                                    )}
                                </Box>
                                <Box display="flex" alignItems="center" flexDirection="column">
                                    <Typography fontSize="12px" variant="body2" color="grey.600">
                                        {flightData.duration}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                                        <Box height="1px" width="142px" bgcolor="grey.600" />
                                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                                    </Box>
                                    <Box display="flex" alignItems="center" flexDirection="row">
                                        <Typography
                                            fontSize="12px"
                                            variant="body2"
                                            color={flightData.stops === 0 ? ' #16a34a' : 'grey.600'}
                                            fontWeight={flightData.stops === 0 ? 600 : 400}
                                        >
                                            {flightData.stops === 0
                                                ? tCommon('direct')
                                                : `${flightData.stops} ${flightData.stops > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                                        </Typography>
                                        {flightData.via && (
                                            <>
                                                <CircleIcon sx={{ fontSize: 5, color: 'grey.600', mx: 0.5 }} />
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: 'textColors.primary'
                                                    }}
                                                >
                                                    {flightData.via}
                                                </Typography>
                                            </>
                                        )}
                                        {/* Layover Icon (if present) */}
                                        {Array.isArray(flightData.layovers) &&
                                            flightData.layovers.length > 0 &&
                                            (() => {
                                                const layover = flightData.layovers[0]
                                                const { type } = getLayoverTypeAndMessage(layover.durationMins)
                                                const LayoverIcon =
                                                    type === 'longLayover' ? LongLayoverIcon : ShortLayoverIcon
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography fontWeight={600} fontSize={"12px"}>
                                                                    {tFlightDetails(type)}
                                                                </Typography>
                                                                <Typography fontWeight={400} fontSize={"10px"}>
                                                                    {tFlightDetails('layover')}: {layover.durationStr}{' '}
                                                                    in {layover.city}
                                                                </Typography>
                                                                <Box display="flex" alignItems="center">
                                                                    <Typography fontWeight={400} fontSize={"10px"}>
                                                                        {
                                                                            getLayoverTypeAndMessage(
                                                                                layover.durationMins
                                                                            ).message
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        }
                                                        placement="top"
                                                    >
                                                        <Box
                                                            display="inline-flex"
                                                            alignItems="center"
                                                            sx={{ cursor: 'help', ml: 1 }}
                                                        >
                                                            <LayoverIcon />
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            })()}

                                        {flightData.noBaggage && (
                                            <Tooltip
                                                title={
                                                    <Box>
                                                        <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                            {tFlightDetails('noBaggageTitle')}
                                                        </Typography>
                                                        <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize={"10px"}>
                                                            {tFlightDetails('noBaggageCta')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                placement="top"
                                            >
                                                <Box sx={{ display: 'inline-flex', cursor: 'help', ml: 1, color: 'grey.600' }}>
                                                    <NoLuggageOutlinedIcon fontSize='small' color='inherit' />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                                <Box display="flex">
                                    <Box display="flex" alignItems="center" flexDirection="column" width="60px">
                                        <Typography
                                            variant="h6"
                                            component="span"
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 700,
                                                color: 'black',
                                                display: 'flex',
                                                flexDirection: 'row'
                                            }}
                                        >
                                            {flightData.arrivalTime}
                                        </Typography>
                                        {flightData.isNbArrival ? (
                                            <Tooltip
                                                title={
                                                    <Box>
                                                        <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                            {tCommon('nearbyAirport')}
                                                        </Typography>
                                                        <Typography fontWeight={500} color="inherit" fontSize={"10px"}>{flightData.arrivalAirport}- {flightData.arrivalAirportName}</Typography>
                                                        <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize={"10px"}>
                                                            {tFlightDetails('nearbyAirportCta')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                placement="top"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        color: '#184E91',
                                                        background: '#EFF6FF',
                                                        px: 1,
                                                        borderRadius: 1,
                                                        display: 'inline-block',
                                                        cursor: 'help'
                                                    }}
                                                >
                                                    {flightData.arrivalAirport}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: 'grey.600',
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {flightData.arrivalAirport}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box minWidth={'40px'} gap={0.5} px={0.5} display="flex" flexDirection={'column'}>
                                        {flightData.isNextDayCallOut && (
                                            <Typography
                                                variant="body2"
                                                fontSize={12}
                                                px={1}
                                                py={0.3}
                                                fontWeight={600}
                                                color="#184E91"
                                                alignItems="center"
                                                //  pt={0.5}
                                                bgcolor="#EFF6FF"
                                                borderRadius={1}
                                            >
                                                {formatDate(
                                                    getFormattedDate(flightData.arrivalDate, 'MM/DD/YYYY'),
                                                    'D MMM'
                                                )}
                                            </Typography>
                                        )}

                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right section - Price and actions */}
                    <Box ml={3} flexShrink={0}>
                        <Box display="flex" gap={2} mb={0.5}>
                            {/* Price section */}
                            <Box textAlign="right">
                                {flightData.priceNote && (
                                    // <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography
                                        variant="body2"
                                        color='textSecondary'
                                        sx={{
                                            // color: '',
                                            fontSize: '0.75rem',
                                            // bgcolor: '#f3f3f3',
                                            pb: 0.5,
                                            borderRadius: 1,
                                            fontWeight: 500
                                            // width: 'max-content'
                                        }}
                                    >
                                        {flightData.priceNote}
                                    </Typography>
                                    // </Box>
                                )}
                                <Box display="flex" alignItems="center" gap={1} justifyContent={'flex-end'}>
                                    {/* Seats Left Callout */}
                                    {typeof flightData.seatsLeft === 'number' &&
                                        flightData.seatsLeft > 0 &&
                                        flightData.seatsLeft <= 5 && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    bgcolor: '#fff0f0',
                                                    color: '#c62828',
                                                    borderRadius: 1,
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    px: 1.2,
                                                    py: 0.2,
                                                    ml: 1,
                                                    height: 24,
                                                    lineHeight: 1
                                                }}
                                            >
                                                {flightData.seatsLeft}{' '}
                                                {flightData.seatsLeft > 1
                                                    ? tFlightDetails('seatLeft', { s: 's' })
                                                    : tFlightDetails('seatLeft', { s: '' })}
                                            </Box>
                                        )}
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 700,
                                            color: 'black',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {flightData.pricePrefix} {formatCurrency(Number(flightData.price))}
                                    </Typography>
                                </Box>
                                {flightData.strikeOffPrice && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'grey.500',
                                            textDecoration: 'line-through',
                                            fontWeight: 400,
                                            fontSize: '0.75rem',
                                            mb: 0.5
                                        }}
                                    >
                                        {formatCurrency(flightData.strikeOffPrice)}
                                    </Typography>
                                )}
                                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                                    {/* Coupon details */}
                                    {flightData.couponDetails && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: '#16a34a',
                                                fontWeight: 500
                                            }}
                                        >
                                            {replacePlaceHolderString(
                                                flightData.couponDetails?.message,
                                                flightData.couponDetails
                                            )}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box>
                                {/* Book button */}
                                <Button
                                    variant="customVariantPrimary"
                                    onClick={handleSelectCard}
                                    disabled={false}
                                    sx={{
                                        bgcolor: false ? '#FFD700' : false ? 'grey.300' : 'brand.primary',
                                        color: loading
                                            ? '#2A5298' // Blue text on gold
                                            : 'brand.primaryForeground',
                                        px: '1rem',
                                        py: '0.25rem',
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        minWidth: 100,
                                        minHeight: 0,
                                        height: 40,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        // ...(loading && {
                                        //     bgcolor: '#FFD700 !important', // Force gold color with !important
                                        //     color: '#2A5298 !important',
                                        //     animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                        //     '@keyframes pulse': {
                                        //         '0%, 100%': { opacity: 1 },
                                        //         '50%': { opacity: 0.8 }
                                        //     }
                                        // }),
                                        // '&:hover': loading
                                        //     ? {}
                                        //     : {
                                        //         bgcolor: 'brand.primaryHover',
                                        //         color: 'apple.black',
                                        //         transform: 'translateY(-1px)',
                                        //         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                        //     },
                                        // '&.Mui-disabled': loading
                                        //     ? {
                                        //         bgcolor: '#FFD700 !important',
                                        //         color: '#2A5298 !important'
                                        //     }
                                        //     : {}
                                    }}
                                >
                                    {loading ? tCommon('Loading') : tCommon('select')}
                                </Button>

                                {/* Flight details link */}
                                {onViewDetailsClick && (
                                    <Box textAlign="right">
                                        <Button
                                            variant="text"
                                            onClick={handleViewDetailsClick}
                                            sx={{
                                                color: 'apple.blue',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                    bgcolor: 'transparent'
                                                }
                                            }}
                                        >
                                            {tCommon('flightDetails')} →
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>


                    </Box>
                </Box>
                {smartMetrics && <SmartMetricsBadge metrics={smartMetrics} />}
            </CardContent>
        </Box>
    )
}

export default RtSolutionCard
